import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { QuotaInput } from '../../../components/quota';
import BillingInterval from '../../../components/quota/BillingInterval';
import { plan } from '../../../layout/routes';
import { Feature, getFeatureFromPlan, PlanInterval, SubscriptionSource } from '../../../models';
import { useNavigate, useQuery } from '../../../routes';
import { fromAccounts, fromOrder, fromSubscription, useAppDispatch } from '../../../store';
import { usePlanCartSnapshot } from '../../../hook/usePlanCartSnapshot';
import { getQuota, sendTrackingData, TrackingPayload } from '../../../utils/utils';
import { useGetTrackingCookies } from '../../../hook/useGetTrackingCookies';

interface ProductQuotaSelectionProps {
  productId: string;
  price: Partial<Record<string, number>>;
  initQuota?: number;
  tooltipText?: string;
  disabled: boolean;
}

const ProductQuotaSelection = (
  {
    productId,
    price,
    initQuota,
    tooltipText,
    disabled,
  }: ProductQuotaSelectionProps,
) => {
  const { get } = useQuery();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const subLoading = useSelector(fromSubscription.selectLoading);
  const selectedAccount = useSelector(fromAccounts.selectSelectedAccount);
  const feature = getFeatureFromPlan(productId);
  const prefix = Feature.getFeaturePrefix(feature);
  const initialQuantity = Number(get(`${prefix}Quota`) ?? initQuota ?? 0);
  const cycleParam = get(`${prefix}Cycle`);
  const initialInterval = cycleParam ? PlanInterval.fromString(cycleParam) : null;
  const [quantity, setQuantity] = useState<number>(initialQuantity);
  const [interval, setInterval] = useState<PlanInterval>();
  const subscriptions = useSelector(
    fromSubscription.selectByAccountIdAndFeature(selectedAccount.id, feature!!),
  );
  const hasAnnualPaidSub = subscriptions.some(
    subscription => subscription.source === SubscriptionSource.RECURLY
      && subscription.billingInterval === PlanInterval.YEAR,
  );
  const cartSnapshot = usePlanCartSnapshot();
  const trackingCookies = useGetTrackingCookies();
  const featureKey = useMemo(() => (
    feature ? Feature.toString(feature) : productId), [feature, productId]);

  const trackPlanPageAction = (payload: TrackingPayload) => {
    if (!selectedAccount?.id) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      selectedAccount.id,
      undefined,
      {
        field: payload.field,
        object: payload.object ?? featureKey,
        action: payload.action,
        ...payload,
        ...trackingCookies,
        ...(cartSnapshot ? { cart_snapshot: cartSnapshot } : {}),
      },
    );
  };

  useEffect(() => {
    // return early if subLoading is true or the interval is not set
    if (subLoading || !interval) return;

    const subscription = subscriptions[0];

    // otw, update the product preview request
    const productCode = `${productId}_${PlanInterval.getPlanIntervalCode(interval)}`;
    const quota = getQuota(subscription, quantity, interval);
    dispatch(fromOrder.updateProductPreviewRequest({
      product: {
        productCode,
        quantity: quota,
      },
    }));
  }, [interval, quantity]);

  useEffect(() => {
    if (subLoading) return;
    if (!cycleParam) {
      const hasMonthlyPaidSub = subscriptions.some(
        subscription => subscription.source === SubscriptionSource.RECURLY
          && subscription.billingInterval === PlanInterval.MONTH,
      );
      setInterval(hasMonthlyPaidSub ? PlanInterval.MONTH : PlanInterval.YEAR);
    } else {
      setInterval(PlanInterval.fromString(cycleParam));
    }
  }, [subLoading]);

  const onChangeQuota = (value: number) => {
    setQuantity(value);
    navigate(plan.path, replaceQuery({
      ...queryDictionary(),
      [`${prefix}Quota`]: value,
      [`${prefix}Cycle`]: interval,
    }));

    trackPlanPageAction({
      field: 'quota_input',
      action: 'change_quantity',
      object: featureKey,
      quantity: value,
      billing_interval: interval ? PlanInterval.getPlanIntervalCode(interval) : undefined,
    });
  };

  const onChangeInterval = (newInterval: PlanInterval) => {
    setInterval(newInterval);
    navigate(plan.path, replaceQuery({
      ...queryDictionary(),
      [`${prefix}Quota`]: quantity,
      [`${prefix}Cycle`]: newInterval,
    }));

    trackPlanPageAction({
      field: 'billing_interval',
      action: 'change_interval',
      object: featureKey,
      billing_interval: PlanInterval.getPlanIntervalCode(newInterval),
      quantity,
    });
  };

  return (
    <>
      <Box
        id={`product-item-${productId}-quota`}
        display="flex"
        flexDirection="row"
        alignContent="center"
        alignItems="center"
        minWidth={theme => theme.spacing(45)}
        width="75%"
      >
        <QuotaInput
          defaultQuantity={quantity}
          minQuantity={initQuota ?? 0}
          productId={productId}
          disabled={disabled}
          changeValue={onChangeQuota}
          step={1}
          tooltipTextId={tooltipText}
        />
        <Typography
          variant="body1"
          fontWeight={700}
          fontSize={14}
          paddingX={4}
        >
          <FormattedMessage
            id="page.expand.plan.unit.price"
            values={{
              price: <strong>{price[interval ?? initialInterval]?.toLocaleString('en-US')}</strong>,
              unit: intl.formatMessage({ id: `page.expand.plan.product.${productId}.unit` }),
              interval: PlanInterval.getCycle(interval ?? initialInterval),
            }}
          />
        </Typography>
      </Box>
      <Box
        id={`product-item-${productId}-billing-interval`}
        paddingX={2}
        display="flex"
        alignContent="center"
        alignItems="center"
        justifyContent="flex-end"
        minWidth={theme => theme.spacing(15)}
        width="25%"
      >
        <BillingInterval
          initialInterval={interval ?? initialInterval}
          onChangeInterval={onChangeInterval}
          disabled={disabled || hasAnnualPaidSub}
        />
      </Box>
    </>
  );
};

export default ProductQuotaSelection;
